import * as vp from 'vopay';
import { FunctionComponent, useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAccessTokenFields } from '@/providers/AccessToken.provider';
import useFetchiQ11AccessTokenInfo from '@/hooks/useApi/useFetchiQ11AccessTokenInfo';
import usePostInteracMoneyRequest from '@/hooks/useApi/usePostInteracMoneyRequest';
import useStorage from '@/hooks/useStorage';
import { useDataFetchIndicator } from '@/hooks';
import { useHistoryFields } from '@/providers/History.provider';
import { useInteracEmbedUrlFields } from '@/providers/InteracEmbedURL.provider';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';

type DataProps = {
  origin: string;
  pageCount: number;
};

const ConnectionProcessor: FunctionComponent = () => {
  const history = useHistory();
  const { partialPaymentValues, isFlexiblePayment, paymentType, installmentDetails } = useFlexiblePayment();
  const { state } = useLocation<DataProps>();
  const { PaymentSelectionMethod } = useParams<{ PaymentSelectionMethod: string }>();
  const [, fetchIQ11AccessTokenInfo] = useFetchiQ11AccessTokenInfo({});

  const { interacEmbedUrl, setInteracEmbedUrl, setTransactionId } = useInteracEmbedUrlFields();
  const [, , payInteracMoneyRequest] = usePostInteracMoneyRequest({});
  const { paylink, Token, flow } = usePaylink();

  const storage = useStorage();
  const { setAccessToken, setProcessor, setPaymentMethod, setExtraInfo } = useAccessTokenFields();

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { setPageCount } = useHistoryFields();
  const interacRequestToken = useRef<string | null>(null);

  useEffect(() => {
    const IframeKey = storage.token.getIQ11Token().iQ11Token;

    const processAccessToken = async () => {
      setDataFetchingInfo(true);
      const ForOnboarding = storage.token.getIQ11Token().iQ11ForOnboarding ?? false;

      const accessTokenInfo = await fetchIQ11AccessTokenInfo({ IframeKey, PaymentSelectionMethod, ForOnboarding });
      if (accessTokenInfo.Success) {
        setAccessToken(accessTokenInfo.AccessToken);
        setProcessor(accessTokenInfo.Processor);
        setExtraInfo(accessTokenInfo.ExtraInfo);
        setPaymentMethod(PaymentSelectionMethod);
        setDataFetchingInfo(false);
        setPageCount(state?.pageCount ? state.pageCount : 2);

        if (PaymentSelectionMethod === 'bank') {
          history.push('/connect/bank');
        } else if (PaymentSelectionMethod === 'credit' || PaymentSelectionMethod === 'debitcard') {
          setPageCount(1);
          history.replace({
            pathname: '/connect/card',
          });
        }
      }
    };

    const processInteracEmbedUrl = async () => {
      // Token is single-use — if we've already initiated a request for this token, skip.
      if (interacRequestToken.current === Token) return;
      interacRequestToken.current = Token;

      const maxRetries = 3;
      setDataFetchingInfo(true);
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const interacMoneyRequest = await payInteracMoneyRequest({
            EmailAddress: paylink.ClientAccountID ? '' : paylink.ReceiverEmailAddress,
            Amount:
              isFlexiblePayment && paymentType === vp.FlexiblePaymentMethods.PartialPayment
                ? Number(partialPaymentValues.Amount)
                : isFlexiblePayment && paymentType === 'InstallmentPlan'
                  ? Number(installmentDetails.initialAmount)
                  : Number(paylink.Amount),
            Currency: paylink.Currency,
            RecipientName: paylink.ReceiverName,
            SingleUseToken: Token,
            MessageForRecipient: '',
            GenerateURL: true,
            ClientAccountID: paylink.ClientAccountID,
          });

          if (!interacMoneyRequest.Success) {
            throw new Error(interacMoneyRequest.ErrorMessage || 'Failed to generate Interac Money Request');
          }

          setInteracEmbedUrl(interacMoneyRequest.MoneyRequestURL || '');
          setTransactionId(interacMoneyRequest.TransactionID);
          setDataFetchingInfo(false);
          history.replace({
            pathname: '/connect/email',
            state: { method: 'email', origin: state?.origin },
          });
          return; // Success, exit the retry loop
        } catch (error) {
          console.error(`Interac money request attempt ${attempt} failed:`, error);

          if (attempt < maxRetries) {
            const retryDelay = attempt * 1000; // Ascending delay: 1s, 2s, 3s
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }
      // Reset on failure so a new token can be retried
      interacRequestToken.current = null;
      setDataFetchingInfo(false);
      history.replace('/connect/email/generate-error');
    };
    if (PaymentSelectionMethod === 'email') {
      if (flow === Flow.FUND__SINGLE && paylink.DisplayEmbbedInterac) {
        if (!interacEmbedUrl) {
          if (paylink.InteracEmbedUrl) {
            setDataFetchingInfo(false);
            setInteracEmbedUrl(paylink.InteracEmbedUrl);
            setTransactionId(paylink.InteracTransactionID || '');

            history.replace({
              pathname: '/connect/email',
              state: { method: 'email', origin: state?.origin },
            });
          } else {
            processInteracEmbedUrl();
          }
        } else {
          setDataFetchingInfo(false);
          history.replace({
            pathname: '/connect/email',
            state: { method: 'email', origin: state?.origin },
          });
        }
      } else {
        history.push({ pathname: '/connect/email/form', state: { type: 'add' } });
      }
    } else {
      processAccessToken().catch((error) => console.error(error));
    }
  }, [PaymentSelectionMethod]);

  return <div />;
};

export default ConnectionProcessor;
